// BannerSlider.js
import React from 'react';
import Slider from 'react-slick';
import styled from 'styled-components';
import { FaCheck } from 'react-icons/fa';
import PropTypes from 'prop-types';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

// Styled Components for the Banner
const BannerContainer = styled.div`
  position: relative;
  height: 600px;
  overflow: hidden;
  width: 100%;
  background: #000;

  @media (max-width: 1024px) {
    height: 500px;
  }

  @media (max-width: 768px) {
    height: 400px;
  }

  @media (max-width: 480px) {
    height: 300px;
  }
`;

const BannerImageWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
`;

const BannerImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  transition: transform 0.8s ease;
  
  ${BannerContainer}:hover & {
    transform: scale(1.05);
  }
`;

const BannerOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.9) 0%,
    rgba(255, 255, 255, 0.7) 50%,
    rgba(255, 255, 255, 0.4) 100%
  );
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 0 10%;
  z-index: 2;

  @media (max-width: 768px) {
    flex-direction: column;
    justify-content: center;
    text-align: center;
    padding: 20px;
  }
`;

const ContentBox = styled.div`
  max-width: 600px;
  z-index: 2;
`;

const Badge = styled.div`
  background-color: #5190A6;
  color: white;
  padding: 4px 12px;
  border-radius: 4px;
  display: inline-block;
  margin-bottom: 20px;
  font-size: 14px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const BannerTitle = styled.h1`
  margin: 0;
  font-size: 48px;
  font-weight: 500;
  color: #5190A6;
  line-height: 1.2;
  
  .highlight {
    color: #333333;
  }

  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const BannerSubtitle = styled.p`
  font-size: 16px;
  color: #666;
  margin: 20px 0 0 0;
  line-height: 1.6;
  max-width: 500px;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const SideImageWrapper = styled.div`
  width: 400px;
  height: auto;
  display: block;
  z-index: 1;

  @media (max-width: 1024px) {
    width: 300px;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const SideImage = styled.img`
  width: 100%;
  height: auto;
`;

const AdLabel = styled.span`
  position: absolute;
  top: 40px;
  right: 40px;
  background-color: rgba(81, 144, 166, 0.1);
  color: #5190A6;
  padding: 8px 15px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: bold;
  backdrop-filter: blur(5px);
  border: 1px solid rgba(81, 144, 166, 0.2);
  z-index: 3;
`;

const StyledSliderWrapper = styled.div`
  position: relative;
  width: 100%;

  .slick-slider {
    max-width: 100% !important;
  }

  .slick-dots {
    bottom: 30px;
    z-index: 10;
    
    li {
      margin: 0 6px;
      
      button:before {
        font-size: 12px;
        color: #5190A6;
        opacity: 0.3;
        transition: all 0.3s ease;
      }
      
      &.slick-active button:before {
        opacity: 1;
        color: #5190A6;
        transform: scale(1.2);
      }
    }
  }

  /* Optional: Custom Arrows if needed later */
  .slick-prev, .slick-next {
    z-index: 11;
    width: 40px;
    height: 40px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 50%;
    backdrop-filter: blur(5px);
    transition: all 0.3s ease;
    
    &:hover {
      background: rgba(255, 255, 255, 0.4);
    }
    
    &:before {
      color: #5190A6;
      font-size: 20px;
    }
  }
  
  .slick-prev { left: 30px; }
  .slick-next { right: 30px; }
`;

const BannerSlider = ({ banners }) => {
  const sliderSettings = {
    dots: true,
    infinite: banners.length > 1,
    speed: 1000,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: banners.length > 1,
    autoplaySpeed: 6000,
    arrows: false,
    fade: true,
    pauseOnHover: true,
  };

  if (!banners || banners.length === 0) {
    return null;
  }

  return (
    <StyledSliderWrapper>
      <Slider {...sliderSettings}>
        {banners.map((banner, index) => {
          const imageSrc = banner.Image[0];
          const finalSrc = (typeof imageSrc === 'string' && (imageSrc.startsWith('http') || imageSrc.startsWith('/static')))
            ? imageSrc
            : `${process.env.REACT_APP_IMAGEURL}${imageSrc}`;

          return (
            <div key={index}>
              <BannerContainer>
                <BannerImageWrapper>
                  <BannerImage
                    src={finalSrc}
                    alt={banner.title || `Banner ${index + 1}`}
                  />
                </BannerImageWrapper>
                <BannerOverlay>
                  <ContentBox>
                    {/* <Badge>{banner.badge || 'IPHrx'}</Badge> */}
                    <BannerTitle>
                      {banner.richTitle || (
                        <>
                          {banner.title} <span className="highlight">{banner.highlight}</span>
                        </>
                      )}
                    </BannerTitle>
                    <BannerSubtitle>
                      {banner.description || banner.subtitle || "Experience the best in pharmaceutical care and medical services."}
                    </BannerSubtitle>
                  </ContentBox>
                  {/* {banner.sideImage && (
                    <SideImageWrapper>
                      <SideImage src={banner.sideImage} alt="Feature" />
                    </SideImageWrapper>
                  )} */}
                </BannerOverlay>
                {/* <AdLabel>Ad</AdLabel> */}
              </BannerContainer>
            </div>
          );
        })}
      </Slider>
    </StyledSliderWrapper>
  );
};

BannerSlider.propTypes = {
  banners: PropTypes.arrayOf(
    PropTypes.shape({
      Image: PropTypes.arrayOf(PropTypes.string).isRequired,
      title: PropTypes.string,
      endDate: PropTypes.string,
    })
  ).isRequired,
};

export default BannerSlider;
